import { Badge, Link, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { RESTRICTED_ATTRIBUTES } from '../utils/attribute-keys';
import { isAttributeEnabledIdentifier, type IdentifiersConfig } from '../utils/identifiers';

export type IdentifierBadgeProps = {
  /** The explanation is only offered where a workspace has several identifiers. */
  isMultiIdentifier: boolean;
  peoplePlural: string;
  identifiersSettingsHref: string;
};

/** The "Identifier" badge, with the explanation a multi-identifier workspace needs. */
export function IdentifierBadge({ isMultiIdentifier, peoplePlural, identifiersSettingsHref }: IdentifierBadgeProps) {
  return (
    <Tooltip isDisabled={!isMultiIdentifier} isCustom>
      {(context) => (
        <>
          <Badge
            color="grey"
            cursor="help"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            data-test="attribute-type-label"
          >
            Identifier
          </Badge>
          <TooltipContent>
            An identifier is a unique value used to identify {peoplePlural} when adding, updating, or tracking a
            profile.
            <br />
            <Link href={identifiersSettingsHref} color="inherit" isUnderlined>
              Manage your identifiers
            </Link>
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

type AttributeTypeLabelProps = {
  attribute: string;
  identifiers: IdentifiersConfig | null | undefined;
  /**
   * What the Identifier badge needs, where the surface wants one at all.
   *
   * Absent means no badge: the attribute editor lists identifiers under
   * their own heading, so one beside each would say it twice. Present means
   * the badge *and* its explanation — they arrive together so a surface
   * cannot end up with a badge that explains nothing, which is what two
   * loose optional props allowed.
   */
  identifierBadge?: { isMultiIdentifier: boolean; workspaceId: string };
};

/**
 * The badge next to an attribute name the system gives a meaning of its own:
 * "Identifier" for one the workspace identifies people by, "Reserved" for one
 * it reserves, and nothing for an ordinary attribute.
 */
export function AttributeTypeLabel({ attribute, identifiers, identifierBadge }: AttributeTypeLabelProps) {
  const taxonomy = useProductTaxonomy();

  if (isAttributeEnabledIdentifier(attribute, identifiers)) {
    if (identifierBadge == null) {
      return null;
    }

    return (
      <IdentifierBadge
        isMultiIdentifier={identifierBadge.isMultiIdentifier}
        peoplePlural={taxonomy.term('people.plural')}
        identifiersSettingsHref={generatePath('/workspaces/:workspaceId/settings/edit', {
          workspaceId: identifierBadge.workspaceId,
        })}
      />
    );
  }

  if (!RESTRICTED_ATTRIBUTES.includes(attribute)) {
    return null;
  }

  return (
    <Tooltip isCustom>
      {(context) => (
        <>
          <Badge
            color="grey"
            cursor="help"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            data-test="attribute-type-label"
          >
            Reserved
          </Badge>
          <TooltipContent>
            Some attribute names are reserved for special purposes in our system.
            <br />
            <Link
              href="https://docs.customer.io/journeys/attributes/#reserved-attributes"
              color="inherit"
              isUnderlined
              isExternal
            >
              Learn more about reserved attributes
            </Link>
          </TooltipContent>
        </>
      )}
    </Tooltip>
  );
}
